import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Row, Col, Layout, Button, Space, Statistic, Typography, Segmented, Spin, Modal, Form, Input, InputNumber, notification, Select, Popconfirm, Tooltip, Radio, Progress, theme } from 'antd';
import { ReloadOutlined, CalendarOutlined, PlusOutlined, CalculatorOutlined, ProductOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { salesPlanningApi, productsApi, channelsApi } from '../../api/client';
import PlanningTreeGrid from './components/PlanningTreeGrid';
import { PlanRow, SalesPlan } from './types';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend,
    ReferenceLine
} from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';

dayjs.locale('pl');

const { Content } = Layout;
const { Title, Text } = Typography;

interface Props {
    planId: string;
}

export default function WorkspacePlan({ planId }: Props) {
    const { token } = theme.useToken();
    const queryClient = useQueryClient();
    const [chartMetric] = useState<'Revenue'>('Revenue'); // Revenue only, units removed
    const [addSkuVisible, setAddSkuVisible] = useState(false);
    const [addChannelVisible, setAddChannelVisible] = useState(false);
    const [isCustomChannel, setIsCustomChannel] = useState(false);
    const [addSegmentVisible, setAddSegmentVisible] = useState(false);
    const [editPlanVisible, setEditPlanVisible] = useState(false);
    const [closeMonthVisible, setCloseMonthVisible] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
    const [reforecastMode, setReforecastMode] = useState<'carry-forward' | 'scale-ratio' | 'distribute-gap'>('carry-forward');
    const [form] = Form.useForm();
    const [skuForm] = Form.useForm();
    const [editPlanForm] = Form.useForm();

    // Main workspace data
    const { data, isLoading } = useQuery({
        queryKey: ['sales-plan-workspace', planId],
        queryFn: () => salesPlanningApi.getWorkspace(planId).then(res => res.data),
    });

    // Enhanced KPIs with actuals
    const { data: enhancedKpiData } = useQuery({
        queryKey: ['sales-plan-kpis', planId],
        queryFn: () => salesPlanningApi.getEnhancedKPIs(planId).then(res => res.data),
    });

    // Actuals data for chart
    const { data: actualsData } = useQuery({
        queryKey: ['sales-plan-actuals', planId],
        queryFn: () => salesPlanningApi.getActuals(planId).then(res => res.data),
    });

    const addSkuMutation = useMutation({
        mutationFn: (values: { productId: string }) =>
            salesPlanningApi.addSkuRow(planId, values.productId),
        onSuccess: () => {
            setAddSkuVisible(false);
            skuForm.resetFields();
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            notification.success({ message: 'Dodano produkt do planu' });
        }
    });

    const updateBucketMutation = useMutation({
        mutationFn: (data: { bucketId: string, values: any }) =>
            salesPlanningApi.updateBucket(data.bucketId, data.values),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            queryClient.invalidateQueries({ queryKey: ['sales-plan-kpis', planId] });
        }
    });

    const addChannelMutation = useMutation({
        mutationFn: async (values: { parentId: string, channelId?: string, newChannelName?: string }) => {
            let channelId = values.channelId;
            if (values.newChannelName) {
                const newChannel = await channelsApi.create({ name: values.newChannelName });
                channelId = newChannel.data.id;
            }
            if (!channelId) throw new Error("Channel ID missing");
            return salesPlanningApi.addChannelRow(planId, values.parentId, channelId);
        },
        onSuccess: () => {
            setAddChannelVisible(false);
            setIsCustomChannel(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            notification.success({ message: 'Dodano kanał' });
        }
    });

    const addSegmentMutation = useMutation({
        mutationFn: (values: { parentId: string, name: string, asp: number }) =>
            salesPlanningApi.addSegmentRow(planId, values.parentId, values.name, values.asp),
        onSuccess: () => {
            setAddSegmentVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            notification.success({ message: 'Dodano segment B2B' });
        }
    });

    const updatePlanMutation = useMutation({
        mutationFn: (values: any) => salesPlanningApi.update(planId, values),
        onSuccess: () => {
            setEditPlanVisible(false);
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            queryClient.invalidateQueries({ queryKey: ['sales-plans'] });
            notification.success({ message: 'Zaktualizowano plan' });
        }
    });

    const deletePlanMutation = useMutation({
        mutationFn: () => salesPlanningApi.delete(planId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sales-plans'] });
            notification.success({ message: 'Usunięto plan' });
            window.location.reload();
        }
    });

    // Close Month mutation - THE MISSING ONE!
    const closeMonthMutation = useMutation({
        mutationFn: (params: { month: string; reforecastMode?: 'carry-forward' | 'scale-ratio' | 'distribute-gap' }) =>
            salesPlanningApi.closeMonth(planId, params.month, params.reforecastMode),
        onSuccess: (res) => {
            setCloseMonthVisible(false);
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            queryClient.invalidateQueries({ queryKey: ['sales-plan-kpis', planId] });
            queryClient.invalidateQueries({ queryKey: ['sales-plan-actuals', planId] });
            notification.success({
                message: 'Zamknięto miesiąc',
                description: res.data?.message || 'Snapshot zapisany'
            });
        },
        onError: (err: any) => {
            notification.error({
                message: 'Błąd zamykania miesiąca',
                description: err.response?.data?.message || err.message
            });
        }
    });

    // Delete row mutation
    const deleteRowMutation = useMutation({
        mutationFn: (rowId: string) => salesPlanningApi.deleteRow(rowId),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
            queryClient.invalidateQueries({ queryKey: ['sales-plan-kpis', planId] });
            notification.success({
                message: 'Usunięto wiersz',
                description: `Usunięto ${res.data?.deletedCount || 1} elementów`
            });
        },
        onError: (err: any) => {
            notification.error({
                message: 'Błąd usuwania',
                description: err.response?.data?.message || err.message
            });
        }
    });

    const { data: productsData } = useQuery({
        queryKey: ['products-list-for-plan'],
        queryFn: () => productsApi.getAll({ limit: 1000 }).then(res => res.data),
        enabled: addSkuVisible
    });

    const { data: channelsData } = useQuery({
        queryKey: ['channels-list'],
        queryFn: () => channelsApi.getAll().then(res => res.data),
        enabled: addChannelVisible
    });

    if (isLoading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" tip="Ładowanie workspace..." /></div>;
    if (!data) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" tip="Przygotowywanie danych..." /></div>;

    const { plan, rows = [], kpi = { totalRevenue: 0 } } = data;

    // Use enhanced KPIs if available, fallback to basic
    const displayKpi = enhancedKpiData || {
        ...kpi,
        actualRevenue: 0,
        realization: 0,
        actualMargin: 0,
        marginRealization: 0
    };

    // Generate columns based on time buckets of the first row
    const sampleRow = rows[0] || rows[0]?.children?.[0];
    // Build chart data with Plan vs Actual
    const chartData = (rows[0]?.buckets || []).map((b: any, index: number) => {
        let totalPlanRevenue = 0;
        let totalPlanUnits = 0;

        rows.forEach((row: PlanRow) => {
            const bucket = row.buckets[index];
            if (bucket) {
                totalPlanRevenue += Number(bucket.revenue);
                totalPlanUnits += Number(bucket.units);
            }
        });

        // Get actual data for this month
        const monthKey = dayjs(b.bucketDate).format('YYYY-MM');
        const monthActuals = actualsData?.liveActuals?.[monthKey] || [];
        let totalActualRevenue = 0;
        let totalActualUnits = 0;
        monthActuals.forEach((a: any) => {
            totalActualRevenue += a.revenue;
            totalActualUnits += a.units;
        });

        return {
            name: dayjs(b.bucketDate).format('MMM YYYY'),
            Plan: Math.round(totalPlanRevenue),
            Actual: Math.round(totalActualRevenue),
        };
    });

    const handleAddChannel = (parentId: string) => {
        setSelectedParentId(parentId);
        setAddChannelVisible(true);
    };

    const handleAddSegment = (parentId: string) => {
        setSelectedParentId(parentId);
        setAddSegmentVisible(true);
    };

    const handleDeleteRow = (rowId: string) => {
        deleteRowMutation.mutate(rowId);
    };

    // Get first open month for close action
    const getFirstOpenMonth = () => {
        if (!rows || rows.length === 0) return null;
        const buckets = rows[0].buckets || [];
        // Sort buckets by date to ensure we find the chronological first open month
        const sorted = [...buckets].sort((a, b) => new Date(a.bucketDate).getTime() - new Date(b.bucketDate).getTime());

        for (const b of sorted) {
            // Find first non-locked bucket
            if (!b.isLocked) {
                // Add 2 days to handle timezone artifacts (e.g. Dec 31 23:00 being treated as Dec instead of Jan)
                return dayjs(b.bucketDate).add(2, 'day').startOf('month').toISOString();
            }
        }
        return null;
    };

    const firstOpenMonth = getFirstOpenMonth();
    const firstOpenMonthLabel = firstOpenMonth ? dayjs(firstOpenMonth).format('MMMM YYYY') : 'Brak';

    // KPI color coding
    const getRealizationColor = (value: number) => {
        if (value >= 100) return '#52c41a';
        if (value >= 80) return '#faad14';
        return '#cf1322';
    };

    return (
        <Layout style={{ background: token.colorBgContainer }}>
            {/* Upper Panel: KPI & Charts */}
            <div style={{ padding: '16px 24px', background: token.colorFillQuaternary, borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                <Row gutter={24}>
                    <Col span={10}>
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                            <Space direction="vertical" size="small" style={{ marginBottom: 16 }}>
                                <Space>
                                    <Title level={4} style={{ margin: 0 }}>{plan.name}</Title>
                                    <Button type="text" icon={<EditOutlined />} size="small" onClick={() => { editPlanForm.setFieldsValue(plan); setEditPlanVisible(true); }} />
                                    <Popconfirm title="Czy na pewno usunąć plan?" onConfirm={() => deletePlanMutation.mutate()}>
                                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                    </Popconfirm>
                                </Space>
                                <Text type="secondary">{new Date(plan.periodStart).toLocaleDateString()} - {new Date(plan.periodEnd).toLocaleDateString()}</Text>
                            </Space>

                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Card size="small" bordered={false} style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                        <Statistic
                                            title="Przychód (Plan)"
                                            value={displayKpi.totalRevenue}
                                            precision={0}
                                            suffix="PLN"
                                            valueStyle={{ color: '#1890ff', fontWeight: 600 }}
                                        />
                                        {displayKpi.actualRevenue > 0 && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Actual: {displayKpi.actualRevenue.toLocaleString('pl-PL')} PLN
                                            </Text>
                                        )}
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card size="small" bordered={false} style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                        <Statistic
                                            title="Realizacja Celu"
                                            value={displayKpi.realization || 0}
                                            suffix="%"
                                            precision={1}
                                            valueStyle={{ color: getRealizationColor(displayKpi.realization || 0) }}
                                            prefix={displayKpi.realization >= 100 ? <ArrowUpOutlined /> : displayKpi.realization > 0 ? <ArrowDownOutlined /> : null}
                                        />
                                        <Progress
                                            percent={Math.min(displayKpi.realization || 0, 100)}
                                            size="small"
                                            showInfo={false}
                                            strokeColor={getRealizationColor(displayKpi.realization || 0)}
                                        />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card size="small" bordered={false} style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                        <Statistic
                                            title="Marża Brutto (Plan)"
                                            value={displayKpi.totalMargin || kpi.totalMargin}
                                            precision={0}
                                            suffix="PLN"
                                            valueStyle={{ color: '#52c41a', fontWeight: 600 }}
                                        />
                                        {displayKpi.avgMargin > 0 && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {displayKpi.avgMargin.toFixed(1)}% marży
                                            </Text>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={14}>
                        <div style={{ background: token.colorBgContainer, padding: 16, borderRadius: 8, height: 300 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                <Text strong>Plan vs Actual: Przychód (PLN)</Text>
                            </div>
                            <ResponsiveContainer width="100%" height="90%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                    <YAxis axisLine={false} tickLine={false} />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="Plan"
                                        stroke="#1890ff"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        name="Plan"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="Actual"
                                        stroke="#52c41a"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ r: 4 }}
                                        name="Actual"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Toolbar */}
            <div style={{ padding: '8px 24px', borderBottom: `1px solid ${token.colorBorderSecondary}`, display: 'flex', justifyContent: 'space-between', background: token.colorBgContainer }}>
                <Space>
                    <Button type="primary" icon={<ProductOutlined />} onClick={() => setAddSkuVisible(true)}>Dodaj SKU</Button>
                    <Tooltip title="Przelicza sumy i KPI na podstawie wprowadzonych danych">
                        <Button icon={<CalculatorOutlined />} onClick={() => {
                            queryClient.invalidateQueries({ queryKey: ['sales-plan-workspace', planId] });
                            queryClient.invalidateQueries({ queryKey: ['sales-plan-kpis', planId] });
                        }}>Przelicz</Button>
                    </Tooltip>
                    <Tooltip title={`Zamyka edycję dla miesiąca: ${firstOpenMonthLabel}`}>
                        <Button
                            icon={<CalendarOutlined />}
                            onClick={() => setCloseMonthVisible(true)}
                            disabled={!firstOpenMonth}
                        >
                            Zamknij miesiąc
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            {/* Grid */}
            <Content style={{ padding: 24 }}>
                <PlanningTreeGrid
                    rows={rows}
                    onUpdateBucket={(id, val) => updateBucketMutation.mutate({ bucketId: id, values: val })}
                    onAddChannel={handleAddChannel}
                    onAddSegment={handleAddSegment}
                    onDeleteRow={handleDeleteRow}
                    actualsData={actualsData?.liveActuals}
                />
            </Content>

            {/* Modals */}
            <Modal
                title="Dodaj SKU do Planu"
                open={addSkuVisible}
                onCancel={() => setAddSkuVisible(false)}
                onOk={() => skuForm.validateFields().then(values => addSkuMutation.mutate(values))}
                confirmLoading={addSkuMutation.isPending}
            >
                <Form form={skuForm} layout="vertical">
                    <Form.Item name="productId" label="Szukaj Produktu" rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Wpisz nazwę produktu lub SKU"
                            optionFilterProp="label"
                            options={(Array.isArray(productsData) ? productsData : productsData?.data || []).map((p: any) => ({
                                label: `${p.sku || ''} - ${p.name}`,
                                value: p.id
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Dodaj Kanał Sprzedaży"
                open={addChannelVisible}
                onCancel={() => setAddChannelVisible(false)}
                onOk={() => form.validateFields().then(values => addChannelMutation.mutate({ ...values, parentId: selectedParentId! }))}
                confirmLoading={addChannelMutation.isPending}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="channelId" label="Kanał" rules={[{ required: !isCustomChannel }]}>
                        <Select
                            placeholder="Wybierz kanał"
                            onChange={(val) => setIsCustomChannel(val === 'new')}
                            options={[
                                ...(channelsData || []).map((c: any) => ({ label: c.name, value: c.id })),
                                { label: '+ Inny (Dodaj nowy)', value: 'new' }
                            ]}
                        />
                    </Form.Item>
                    {isCustomChannel && (
                        <Form.Item name="newChannelName" label="Nazwa nowego kanału" rules={[{ required: true }]}>
                            <Input placeholder="Np. Empik, Hurtownia X" />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal
                title="Edytuj Plan"
                open={editPlanVisible}
                onCancel={() => setEditPlanVisible(false)}
                onOk={() => editPlanForm.validateFields().then(values => updatePlanMutation.mutate(values))}
                confirmLoading={updatePlanMutation.isPending}
            >
                <Form form={editPlanForm} layout="vertical">
                    <Form.Item name="name" label="Nazwa Planu" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="period" label="Okres Planowania (Zmiana dat wymaga ponownego przeliczenia)" rules={[{ required: true }]}>
                        <CalendarOutlined style={{ marginRight: 8 }} />
                        <span style={{ color: '#999' }}>Edycja dat wkrótce dostępna (Wymaga przebudowy kubełków)</span>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Dodaj Segment B2B"
                open={addSegmentVisible}
                onCancel={() => setAddSegmentVisible(false)}
                onOk={() => form.validateFields().then(values => addSegmentMutation.mutate({ ...values, parentId: selectedParentId! }))}
                confirmLoading={addSegmentMutation.isPending}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Nazwa Segmentu" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="asp" label="Średnia Cena (ASP)" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Close Month Modal */}
            <Modal
                title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> Zamknij Miesiąc</Space>}
                open={closeMonthVisible}
                onCancel={() => setCloseMonthVisible(false)}
                onOk={() => {
                    if (firstOpenMonth) {
                        closeMonthMutation.mutate({ month: firstOpenMonth, reforecastMode });
                    }
                }}
                confirmLoading={closeMonthMutation.isPending}
                okText="Zamknij miesiąc"
                cancelText="Anuluj"
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>Zamykasz miesiąc: <strong>{firstOpenMonthLabel}</strong></Text>
                    <br />
                    <Text type="secondary">
                        • Zapisany zostanie snapshot aktualnych danych sprzedaży<br />
                        • Komórki dla tego miesiąca zostaną zablokowane<br />
                        • KPI zostaną zaktualizowane o dane rzeczywiste
                    </Text>
                </div>
                <Form layout="vertical">
                    <Form.Item label="Tryb Reforecastu (dla kolejnych miesięcy)">
                        <Radio.Group value={reforecastMode} onChange={(e) => setReforecastMode(e.target.value)}>
                            <Space direction="vertical">
                                <Radio value="carry-forward">
                                    <Text strong>Carry-forward growth</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>Zachowaj planowany wzrost m/m</Text>
                                </Radio>
                                <Radio value="scale-ratio">
                                    <Text strong>Scale ratio-to-plan</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>Przeskaluj pozostałe miesiące proporcjonalnie do realizacji</Text>
                                </Radio>
                                <Radio value="distribute-gap">
                                    <Text strong>Distribute gap</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>Rozłóż niedobór/nadwyżkę na pozostałe miesiące</Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
}
